import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';

import './App.css';
import './responsive.css';
import Admin from './components/Admin';
import AdminDetails from './components/AdminDetails';
import AppUpdate from './components/AppUpdate';
import Banner from './components/Banner';
import BannerDetails from './components/BannerDetails';
import Booking from './components/Booking';
import BookingAlerts from './components/BookingAlerts/BookingAlerts';
import BookingDashboard from './components/BookingDashboard';
import BookingDetails from './components/BookingDetails';
import BookingIntelligence from './components/BookingIntelligence';
import Customer from './components/Customer';
import CustomerDetails from './components/CustomerDetails';
import CustomerList from './components/CustomerList';
import Dashboard from './components/Dashboard';
import Finance from './components/Finance';
import Layout from './components/Layout';
import Login from './components/Login';
import ManualKYC from './components/ManualKYC';
import PackageTypes from './components/PackageTypes';
import ParcelDimensions from './components/ParcelDimensions';
import ParcelWeights from './components/ParcelWeights';
import PaymentAlerts from './components/PaymentAlerts/PaymentAlerts';
import Promotions from './components/Promotions';
import RideList from './components/RideList';
import Role from './components/Role';
import RoleDetails from './components/RoleDetails';
import ShipmentManagement from './components/ShimentManagement';
import TicketDetails from './components/TicketDetails';
import Tickets from './components/Tickets';
import TripMangement from './components/TripManagement';
import VehicleType from './components/VehicleType';
import theme from './muiTheme';
import Bar from './scenes/bar';
import Calendar from './scenes/calendar/calendar';
import Contacts from './scenes/contacts';
import FAQ from './scenes/faq';
import Form from './scenes/form';
import Geography from './scenes/geography';
import Invoices from './scenes/invoices';
import Line from './scenes/line';
import Pie from './scenes/pie';
import Team from './scenes/team';
import logger from './utils/logger';

// Suppress MUI capitalize warnings permanently

const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  const message = String(args[0] || '');
  if (message.includes('capitalize') || message.includes('MUI:')) {
    return;
  }
  originalConsoleWarn.apply(console, args);
};

const originalConsoleError = console.error;
console.error = (...args) => {
  const message = String(args[0] || '');
  if (message.includes('capitalize') || message.includes('MUI:')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// Error boundary for MUI issues
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (!error.message.includes('capitalize')) {
      logger.error('Error caught by boundary:', { error, errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong.</div>;
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <HashRouter>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/booking-intelligence"
                element={<BookingIntelligence />}
              />
              <Route path="/manual-kyc" element={<ManualKYC />} />
              <Route path="/payment-alerts" element={<PaymentAlerts />} />
              <Route path="/booking-alerts" element={<BookingAlerts />} />
              <Route path="/team" element={<Team />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/form" element={<Form />} />
              <Route path="/bar" element={<Bar />} />
              <Route path="/pie" element={<Pie />} />
              <Route path="/line" element={<Line />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/geography" element={<Geography />} />
              <Route path="/customer-list" element={<CustomerList />} />
              <Route path="/ride-list" element={<RideList />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/:adminId" element={<AdminDetails />} />
              <Route path="/customers" element={<Customer />} />
              <Route
                path="/customers/:customerId"
                element={<CustomerDetails />}
              />
              <Route path="/role" element={<Role />} />
              <Route path="/role/:roleId" element={<RoleDetails />} />
              <Route path="/vehicletype" element={<VehicleType />} />
              <Route path="/parcelweights" element={<ParcelWeights />} />
              <Route path="/packagetypes" element={<PackageTypes />} />
              <Route path="/promotions" element={<Promotions />} />
              <Route path="/parceldimensions" element={<ParcelDimensions />} />
              <Route path="/banners" element={<Banner />} />
              <Route path="/banners/:bannerId" element={<BannerDetails />} />
              <Route path="/trips" element={<TripMangement />} />
              <Route path="/shipments" element={<ShipmentManagement />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:ticketId" element={<TicketDetails />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/booking/:bookingId" element={<BookingDetails />} />
              <Route path="/booking-dashboard" element={<BookingDashboard />} />
              <Route path="/finance" element={<Finance />} />
              <Route path="/app-update" element={<AppUpdate />} />
            </Route>
          </Routes>
        </HashRouter>
        <ToastContainer />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
